import { layersFormattedAtom } from '@app/domains/layers/state/layers.atoms'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

export type ElementGroupBucket = Record<string, string[]>

const getDefaultElementGroup = (elementId: string): string => `Unknown element group (${elementId})`

const assetMatchesElementId = (asset: unknown, elementId: string): boolean => {
  if (asset === null || typeof asset !== 'object') return false

  const candidateValues = [
    'type' in asset ? asset.type : undefined,
    'assetTag' in asset ? asset.assetTag : undefined,
    'key' in asset ? asset.key : undefined,
    'value' in asset ? asset.value : undefined,
    'id' in asset ? asset.id : undefined,
  ]

  return candidateValues.some((value) => value === elementId)
}

const getElementGroup = (elementGroups: unknown, elementId: string): string => {
  if (elementGroups === null || typeof elementGroups !== 'object')
    return getDefaultElementGroup(elementId)

  for (const [groupName, groupConfig] of Object.entries(elementGroups)) {
    if (groupConfig === null || typeof groupConfig !== 'object') continue

    const groups = 'groups' in groupConfig ? groupConfig.groups : undefined
    if (!Array.isArray(groups)) continue

    const hasElement = groups.some((group) => {
      if (group === null || typeof group !== 'object') return false

      const assets = 'assets' in group ? group.assets : undefined

      return (
        Array.isArray(assets) && assets.some((asset) => assetMatchesElementId(asset, elementId))
      )
    })

    if (hasElement) return groupName
  }

  return getDefaultElementGroup(elementId)
}

export const useElementGroupBuckets = (elementIds: string[]): ElementGroupBucket[] => {
  const layers = useAtomValue(layersFormattedAtom)

  return useMemo(() => {
    const bucketsByGroup = new Map<string, string[]>()

    for (const elementId of elementIds) {
      const group = getElementGroup(layers.elements, elementId)
      const currentBucket = bucketsByGroup.get(group) ?? []

      bucketsByGroup.set(group, [...currentBucket, elementId])
    }

    return Array.from(bucketsByGroup.entries()).map(([group, groupedElementIds]) => ({
      [group]: groupedElementIds,
    }))
  }, [elementIds, layers.elements])
}
